import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/shared";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, MapPin, Phone, Trash2, Building2, Power } from "lucide-react";

const EMPTY = { name: "", branch_code: "", city: "", address: "", phone: "", whatsapp: "", gst_no: "", maps_link: "", landmark: "", opening_time: "10:00", closing_time: "21:00", weekly_off: "Monday", status: "active", image: "" };

export default function Branches() {
  const { user } = useAuth();
  const canManage = ["OWNER", "SUPER_ADMIN"].includes(user?.role);
  const [branches, setBranches] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [delTarget, setDelTarget] = useState(null);
  const [makeAccounts, setMakeAccounts] = useState(false);
  const emptyAcc = (role) => ({ name: "", email: "", phone: "", password: "", role, status: "active" });
  const [mgr, setMgr] = useState(emptyAcc("BRANCH_MANAGER"));
  const [rec, setRec] = useState(emptyAcc("RECEPTION"));

  const load = () => api.get("/branches", { params: { include_inactive: true } }).then((r) => setBranches(r.data));
  useEffect(() => { load(); }, []);

  const reactivate = async (b) => {
    try { await api.put(`/branches/${b.id}`, { ...EMPTY, ...b, status: "active" }); toast.success("Branch reactivated"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const createAccount = async (acc, branchId) => {
    if (!acc.email.trim() || !acc.password.trim() || !acc.name.trim()) return null;
    await api.post("/users", { name: acc.name, email: acc.email.trim().toLowerCase(), password: acc.password,
      role: acc.role, branch_id: branchId, phone: acc.phone });
    return acc.role;
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Branch name is required"); return; }
    try {
      let branchId = editId;
      if (editId) await api.put(`/branches/${editId}`, form);
      else { const { data } = await api.post("/branches", form); branchId = data.id; }
      if (!editId && makeAccounts && branchId) {
        try {
          const made = [];
          const m = await createAccount(mgr, branchId); if (m) made.push("Manager");
          const r = await createAccount(rec, branchId); if (r) made.push("Receptionist");
          if (made.length) toast.success(`Branch saved · ${made.join(" & ")} account${made.length > 1 ? "s" : ""} created`);
          else toast.success("Branch saved");
        } catch (e) { toast.error("Branch saved, but account creation failed: " + apiError(e.response?.data?.detail)); }
      } else { toast.success("Branch saved"); }
      setOpen(false); setMakeAccounts(false); setMgr(emptyAcc("BRANCH_MANAGER")); setRec(emptyAcc("RECEPTION")); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const confirmDelete = async () => {
    try {
      const { data } = await api.delete(`/branches/${delTarget.id}`);
      toast.success(data.archived ? "Branch archived (data preserved)" : "Branch deleted");
      setDelTarget(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title="Branches" subtitle="Multi-branch salon network"
        actions={canManage && <Button onClick={() => { setForm(EMPTY); setEditId(null); setOpen(true); }} data-testid="add-branch-btn" className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]"><Plus size={16} className="mr-1" /> Add Branch</Button>} />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {branches.map((b) => (
          <Card key={b.id} className="p-5 bg-card border-border" data-testid={`branch-card-${b.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-display text-xl">{b.name}</h3>
                {(b.branch_code || b.city) && <p className="text-xs text-muted-foreground mt-0.5">{[b.branch_code, b.city].filter(Boolean).join(" · ")}</p>}
              </div>
              {canManage && (
                <div className="flex items-center gap-1">
                  {b.status === "inactive" && <Button size="icon" variant="ghost" className="h-8 w-8 text-green-400 hover:bg-green-500/10" title="Reactivate branch" onClick={() => reactivate(b)} data-testid={`reactivate-branch-${b.id}`}><Power size={14} /></Button>}
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => { setForm({ ...EMPTY, ...b }); setEditId(b.id); setOpen(true); }} data-testid={`edit-branch-${b.id}`}><Pencil size={14} /></Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-red-400 hover:text-red-300 hover:bg-red-500/10" onClick={() => setDelTarget(b)} data-testid={`delete-branch-${b.id}`}><Trash2 size={14} /></Button>
                </div>
              )}
            </div>
            <p className="text-sm text-muted-foreground mt-2 flex items-center gap-1"><MapPin size={13} /> {b.address || "—"}</p>
            <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1"><Phone size={13} /> {b.phone || "—"}</p>
            {b.gst_no && <p className="text-xs text-muted-foreground mt-1" data-testid={`branch-gst-${b.id}`}>GST: {b.gst_no}</p>}
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              <Badge variant="outline" className="border-primary text-primary">{b.opening_time}–{b.closing_time}</Badge>
              <Badge variant="outline">Off: {b.weekly_off}</Badge>
              <Badge variant="outline" className={b.status === "active" ? "text-green-300 border-green-800" : "text-muted-foreground"}>{b.status}</Badge>
            </div>
          </Card>
        ))}
      </div>

      {/* Add / Edit */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editId ? "Edit" : "Add"} Branch</DialogTitle>
            <DialogDescription className="sr-only">Enter the branch details and save.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Branch Name</Label><Input data-testid="branch-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-secondary mt-1" /></div>
              <div><Label className="text-xs">Branch Code</Label><Input data-testid="branch-code" value={form.branch_code} onChange={(e) => setForm({ ...form, branch_code: e.target.value })} className="bg-secondary mt-1" placeholder="e.g. DS-01" /></div>
            </div>
            <div><Label className="text-xs">Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="bg-secondary mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">City</Label><Input data-testid="branch-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="bg-secondary mt-1" /></div>
              <div><Label className="text-xs">Contact Number</Label><Input data-testid="branch-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-secondary mt-1" /></div>
            </div>
            <div><Label className="text-xs">Google Maps Location Link</Label><Input data-testid="branch-maps-link" value={form.maps_link} onChange={(e) => setForm({ ...form, maps_link: e.target.value })} className="bg-secondary mt-1" placeholder="https://maps.app.goo.gl/..." /></div>
            <div><Label className="text-xs">Location / Landmark</Label><Input data-testid="branch-landmark" value={form.landmark} onChange={(e) => setForm({ ...form, landmark: e.target.value })} className="bg-secondary mt-1" placeholder="e.g. Near KIMS Hospital, Pillar No. 44" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">WhatsApp</Label><Input value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} className="bg-secondary mt-1" /></div>
              <div><Label className="text-xs">GST Number (on invoices)</Label><Input data-testid="branch-gst" value={form.gst_no} onChange={(e) => setForm({ ...form, gst_no: e.target.value })} className="bg-secondary mt-1" placeholder="36AAUFD9815G1ZX" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Opening</Label><Input value={form.opening_time} onChange={(e) => setForm({ ...form, opening_time: e.target.value })} className="bg-secondary mt-1" /></div>
              <div><Label className="text-xs">Closing</Label><Input value={form.closing_time} onChange={(e) => setForm({ ...form, closing_time: e.target.value })} className="bg-secondary mt-1" /></div>
              <div><Label className="text-xs">Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="bg-secondary mt-1" data-testid="branch-status"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="text-xs">Weekly Off</Label>
              <Select value={form.weekly_off} onValueChange={(v) => setForm({ ...form, weekly_off: v })}>
                <SelectTrigger className="bg-secondary mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          {!editId && (
            <div className="mt-3 rounded-sm border border-primary/30 bg-primary/5 p-3 space-y-3" data-testid="branch-accounts-section">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={makeAccounts} onChange={(e) => setMakeAccounts(e.target.checked)} data-testid="branch-make-accounts" />
                <span className="text-sm font-medium">Create Branch Accounts (Manager & Receptionist)</span>
              </label>
              {makeAccounts && (
                <div className="space-y-4">
                  {[{ k: "mgr", val: mgr, set: setMgr, title: "Branch Manager" }, { k: "rec", val: rec, set: setRec, title: "Receptionist" }].map((acc) => (
                    <div key={acc.k} className="rounded-sm border border-border p-3 space-y-2">
                      <div className="text-xs font-medium text-primary">{acc.title} — locked to this branch</div>
                      <div className="grid grid-cols-2 gap-2">
                        <Input placeholder="Full Name" value={acc.val.name} onChange={(e) => acc.set({ ...acc.val, name: e.target.value })} className="bg-secondary" data-testid={`${acc.k}-name`} />
                        <Input placeholder="Mobile" value={acc.val.phone} onChange={(e) => acc.set({ ...acc.val, phone: e.target.value })} className="bg-secondary" data-testid={`${acc.k}-phone`} />
                        <Input placeholder="Login Email" value={acc.val.email} onChange={(e) => acc.set({ ...acc.val, email: e.target.value })} className="bg-secondary" data-testid={`${acc.k}-email`} />
                        <Input placeholder="Password" type="text" value={acc.val.password} onChange={(e) => acc.set({ ...acc.val, password: e.target.value })} className="bg-secondary" data-testid={`${acc.k}-password`} />
                      </div>
                    </div>
                  ))}
                  <p className="text-xs text-muted-foreground">Leave a block blank to skip it. Permissions are managed per account in Settings → Manager Accounts (the shield icon on each row).</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter><Button onClick={save} data-testid="branch-save" className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete / Archive confirm */}
      <Dialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <DialogContent className="bg-card border-border max-w-md" data-testid="branch-delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2"><Building2 size={20} className="text-red-400" /> Delete this branch?</DialogTitle>
            <DialogDescription className="text-amber-300/90">
              This branch may contain business data. Deleting it may affect historical records.
              If it has bills, staff, appointments, attendance, salary, expenses, inventory or incentive
              records, it will be <b>archived (deactivated)</b> instead — its past reports stay accessible.
              Only completely empty branches are permanently deleted.
            </DialogDescription>
          </DialogHeader>
          {delTarget && <p className="text-sm">Branch: <span className="font-medium">{delTarget.name}</span></p>}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDelTarget(null)} data-testid="branch-delete-cancel">Cancel</Button>
            <Button onClick={confirmDelete} className="rounded-sm bg-red-600 text-white hover:bg-red-700" data-testid="branch-delete-confirm">Delete / Archive</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
